import { useMemo, useState } from "react"
import MaterialReactTable from "material-react-table"
import { MRT_Localization_ES } from "material-react-table/locales/es"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import RefreshIcon from "@mui/icons-material/Refresh"
import ComprobanteActions from "./ComprobanteActions.js"
import EmptyState from "./EmptyState.js"
import {
  formatCurrency,
  formatDate,
  formatComprobanteNumber,
  formatCuit,
  getEstado,
  getEstadoConfig,
} from "../utils/formatters.js"
import moment from 'moment';

function MoneyCell({ value, strong = false }) {
  return (
    <Box
      sx={{
        width: "100%",
        textAlign: "right",
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: strong ? 700 : 500,
        color: strong ? "text.primary" : "text.secondary",
      }}
    >
      {formatCurrency(value)}
    </Box>
  )
}

function ComprobantesTable({ data, isLoading, isError, onRowAction, onRefresh }) {
  const columns = useMemo(
    () => [
      {
        // accessorFn: (row) => formatDate(row.fecha_emision),
        accessorFn: (row) => moment(row.fecha_emision).format("DD-MM-YYYY"),
        id: "fecha_emision",
        header: "Fecha",
        size: 80,
        enableColumnFilter: true,
      },
      {
        accessorKey: "sucursal",
        id: "sucursal",
        header: "Suc.",
        size: 80,
        enableColumnFilter: true,
      },
      {
        accessorKey: "texto_comprobante",
        header: "Tipo",
        size: 150,
      },
      {
        accessorKey: "letra",
        header: "Letra",
        size: 55,
        Cell: ({ cell }) => {
          const letra = cell.getValue()
          return letra ? (
            <Chip
              label={letra}
              size="small"
              variant="filled"
              color="primary"
              sx={{ fontWeight: 700, minWidth: 34}}
            />
          ) : (
            "-"
          )
        },
      },
      {
        accessorFn: (row) => formatComprobanteNumber(row.pto_venta, row.nro_comprobante),
        id: "nro_comprobante",
        header: "N°",
        size: 120,
        Cell: ({ cell }) => (
          <Box sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 500 }}>{cell.getValue()}</Box>
        ),
      },
      // {
      //   accessorKey: "nro_sisven",
      //   header: "N° Sisven",
      //   size: 40,
      //   Cell: ({ cell }) => (
      //     <Tooltip title={cell.getValue() ?? ""}>
      //       <Typography variant="body2" noWrap sx={{ maxWidth: 240, fontWeight: 500 }}>
      //         {cell.getValue() || "-"}
      //       </Typography>
      //     </Tooltip>
      //   ),
      // },
      {
        accessorKey: "razon_social",
        header: "Cliente",
        size: 250,
        Cell: ({ cell, row }) => (
          <Tooltip title={row.original.razon_social_real}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 240, fontWeight: 500 }}>
                  {cell.getValue() || "-"} ({row.original.sisven_id || row.original.sisven_id_real || "No registrado"})
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorFn: (row) => formatCuit(row.cuit_dni),
        id: "cuit_dni",
        header: "CUIT / DNI",
        size: 130,
        Cell: ({ cell }) => (
          <Box sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{cell.getValue()}</Box>
        ),
      },
      {
        accessorKey: "importe_total",
        header: "Total",
        size: 140,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        Cell: ({ cell }) => <MoneyCell value={cell.getValue()} strong />,
      },
      {
        // accessorFn: (row) => getEstado(row),
        accessorKey: "estado",
        id: "estado",
        header: "Estado",
        size: 90,
        filterVariant: "select",
        Cell: ({ cell }) => {
          const config = getEstadoConfig(cell.getValue())
          return <Chip size="small" 
            label={cell.getValue()} 
            color={config.color} 
            />
        },
      },
    ],
    [],
  )

  const [pagination, setPagination]=useState({ 
    pageSize: 5, 
    pageIndex: 0 
  });

  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      localization={MRT_Localization_ES}
      enableColumnResizing
      columnResizeMode="onChange"
      enablePinning
      enableColumnFilters
      enableGlobalFilter
      // enableColumnOrdering
      enableHiding
      enableDensityToggle
      enableFullScreenToggle
      enableStickyHeader
      enableColumnActions
      enableRowActions
      positionActionsColumn="last" 
      onPaginationChange={setPagination}
      autoResetPageIndex={false}
      state={{
        isLoading,
        showAlertBanner: isError,
        showProgressBars: isLoading,
        pagination
      }}
      muiToolbarAlertBannerProps={
        isError
          ? {
              color: "error",
              children: "Ocurrió un error al obtener los comprobantes. Reintentá nuevamente.",
            }
          : undefined
      }
      layoutMode="grid"
      muiTablePaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        },
      }}
      muiTableContainerProps={{ sx: { maxHeight: "62vh" } }}
      muiTableHeadCellProps={{
        sx: {
          bgcolor: "#f8fafc",
          fontWeight: 700,
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        },
      }}
      muiTableBodyRowProps={{
        hover: true,
        sx: {
          "&:hover td": { bgcolor: "action.hover" },
        },
      }}
      muiTableBodyCellProps={{ sx: { borderColor: "divider" } }}
      muiSearchTextFieldProps={{
        placeholder: "Buscar en todos los campos...",
        size: "small",
        sx: { minWidth: 280 },
        variant: "outlined",
      }}
      enablePagination
      muiTablePaginationProps={{
        rowsPerPageOptions: [5,10, 25, 50, 100],
        showFirstButton: true,
        showLastButton: true,
      }}
      initialState={{
        density: "compact",
        showGlobalFilter: true,
        pagination: { pageSize: 5, pageIndex: 0 },
        columnPinning: { left: ["mrt-row-actions"], left: [] },
      }}
      displayColumnDefOptions={{
        "mrt-row-actions": {
          header: "Acciones",
          size: 60,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
        },
      }}
      renderRowActions={({ row }) => (
        <ComprobanteActions comprobante={row.original} onAction={onRowAction} />
      )}
      renderTopToolbarCustomActions={({ table }) => {
        const total = table.getPrePaginationRowModel().rows.length
        return (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pl: 0.5 }}>
            <Chip
              label={`${total} ${total === 1 ? "comprobante" : "comprobantes"}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Tooltip title="Refrescar">
              <IconButton size="small" onClick={onRefresh} aria-label="Refrescar">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }}
      renderEmptyRowsFallback={() => (isError ? null : <EmptyState />)}
    />
  )
}

export default ComprobantesTable
